import { useTranslation } from 'react-i18next';
import { Alert } from '@heroui/alert';
import { Button } from '@heroui/button';
import { useNavigate, useParams } from 'react-router-dom';
import classNames from 'classnames';
import { useDisclosure } from '@heroui/modal';
import { MdEdit } from 'react-icons/md';

import DefaultLayout from '@/layouts/default.tsx';
import Container from '@/components/Container/Container.tsx';
import Breadcrumbs from '@/components/Breadcrumbs/Breadcrumbs.tsx';
import { useSongbook } from '@/hooks/useSongbook.ts';
import { songbookBg } from '@/core/color.ts';

import ExportButton from '../components/ExportButton.tsx';

import SongbookEditModal from './SongbookEditModal.tsx';
import SongsList from './SongsList.tsx';
import ImportButton from '@/pages/Songbook/components/ImportButton.tsx';

function SongbookDetail() {
  const { t } = useTranslation();
  const { songbookId = '' } = useParams();
  const { isLoading, data: songbook, error } = useSongbook(songbookId);
  const navigate = useNavigate();
  const disclosure = useDisclosure();

  if (isLoading) {
    return (
      <DefaultLayout>
        <div className="bg-content2 h-20" />
      </DefaultLayout>
    );
  }

  if (error || !songbook) {
    return (
      <DefaultLayout>
        <Container>
          <div className="flex justify-center my-8">
            <Alert
              className="sm:max-w-1/2"
              color="danger"
              description={t('songbook.detail.errorDescription')}
              endContent={
                <Button color="danger" size="sm" variant="flat" onPress={() => navigate('/songbook')}>
                  {t('songbook.detail.goBack')}
                </Button>
              }
              title={t('songbook.detail.errorTitle')}
              variant="faded"
            />
          </div>
        </Container>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout>
      <div className={classNames('bg-content2', songbookBg(songbook))}>
        <Container>
          <div className="h-20 flex justify-between items-center">
            <h1 className="text-3xl">
              {songbook.title}
            </h1>
            <div className="flex gap-2">
              <ExportButton
                color={songbook.color}
                songbookId={songbook.id}
                variant="solid"
              />
              <ImportButton
                color={songbook.color}
                songbookId={songbook.id}
                variant="solid"
              />
              <Button
                color={songbook.color}
                startContent={<MdEdit />}
                variant="solid"
                onPress={disclosure.onOpen}
              >
                {t('songbook.detail.edit')}
              </Button>
            </div>
          </div>
        </Container>
      </div>
      <Container className="flex grow flex-col">
        <div className="my-4">
          <Breadcrumbs
            items={[
              { href: '/songbook', value: 'Songbooks' },
              { href: `/songbook/${songbook.id}`, value: songbook.title },
            ]}
          />
        </div>

        <SongsList songbook={songbook} />
      </Container>
      <SongbookEditModal disclosure={disclosure} songbook={songbook} />
    </DefaultLayout>
  );
}

export default SongbookDetail;
